import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid,
} from "recharts";
import { FlaskConical, TrendingUp, Users, Activity, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const STAGE_COLORS = {
    "Preclinical": "#8A9A86",
    "Phase I": "#A8B5A3",
    "Phase II": "#8A9A86",
    "Phase III": "#C0604A",
    "Pre-registration": "#B45309",
    "Approved": "#166534",
    "Marketed": "#1A4331",
};

// Hoisted constants — passing inline objects to recharts props forces it to
// re-render its internals on every parent render, even when data is identical.
const CHART_MARGIN = { top: 5, right: 10, left: -20, bottom: 5 };
const AXIS_TICK_STYLE = { fill: "#475569", fontSize: 12 };
const TOOLTIP_CURSOR = { fill: "rgba(26, 67, 49, 0.06)" };
const TOOLTIP_STYLE = { background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 };
const BAR_RADIUS = [6, 6, 0, 0];

function Kpi({ label, value, icon: Icon, accent, testid }) {
    return (
        <Card className="p-6 card-lift border-[#E2E8F0] bg-white fade-in-up" data-testid={testid}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs uppercase tracking-widest text-[#8A9A86]">{label}</p>
                    <p className="font-heading text-4xl font-semibold text-[#1A4331] mt-3">{value}</p>
                </div>
                <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: accent + "18", color: accent }}
                >
                    <Icon className="w-5 h-5" />
                </div>
            </div>
        </Card>
    );
}

export default function DashboardHome() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/stats")
            .then((r) => setStats(r.data))
            .finally(() => setLoading(false));
    }, []);

    if (loading || !stats) {
        return (
            <div className="p-8">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 w-64 bg-[#E2E8F0] rounded" />
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-white rounded-lg border border-[#E2E8F0]" />)}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-10 max-w-7xl">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-8 fade-in-up">
                <div>
                    <p className="text-xs uppercase tracking-widest text-[#8A9A86]">Overview</p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1A4331] mt-2">Sales Intelligence</h1>
                    <p className="text-[#475569] mt-3 max-w-2xl">
                        UK pharmaceutical pipeline intelligence curated for the Royal College of Nursing commercial team.
                        Tap any drug to generate AI-powered spend likelihood scorecards.
                    </p>
                </div>
                <Link to="/drugs">
                    <span className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-[#1A4331] text-white hover:bg-[#123022] transition-colors font-medium text-sm" data-testid="open-drugs-btn">
                        <FlaskConical className="w-4 h-4" /> Explore pipeline
                    </span>
                </Link>
            </div>

            {/* Bento KPI grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Kpi label="Drugs Tracked" value={stats.total_drugs} icon={FlaskConical} accent="#1A4331" testid="kpi-total-drugs" />
                <Kpi label="High Likelihood" value={stats.high_likelihood} icon={TrendingUp} accent="#C0604A" testid="kpi-high-likelihood" />
                <Kpi label="Assigned" value={stats.assigned} icon={Users} accent="#8A9A86" testid="kpi-assigned" />
                <Kpi label="Approved / Marketed" value={stats.approved} icon={Activity} accent="#166534" testid="kpi-approved" />
            </div>

            {/* Bento charts */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <Card className="lg:col-span-3 p-6 bg-white border-[#E2E8F0] fade-in-up stagger-2">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <p className="text-xs uppercase tracking-widest text-[#8A9A86]">Pipeline</p>
                            <h2 className="font-heading text-2xl font-medium text-[#1A4331] mt-1">Development Stages</h2>
                        </div>
                        <Badge className="bg-[#F1F5F9] text-[#1E293B] hover:bg-[#F1F5F9]">MHRA + EMA</Badge>
                    </div>
                    <div className="h-72" data-testid="stage-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.stage_breakdown} margin={CHART_MARGIN}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="stage" tick={AXIS_TICK_STYLE} axisLine={false} tickLine={false} />
                                <YAxis tick={AXIS_TICK_STYLE} axisLine={false} tickLine={false} />
                                <Tooltip cursor={TOOLTIP_CURSOR} contentStyle={TOOLTIP_STYLE} />
                                <Bar dataKey="count" radius={BAR_RADIUS}>
                                    {stats.stage_breakdown.map((entry) => (
                                        <Cell key={entry.stage} fill={STAGE_COLORS[entry.stage] || "#1A4331"} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card className="lg:col-span-2 p-6 bg-white border-[#E2E8F0] fade-in-up stagger-3">
                    <p className="text-xs uppercase tracking-widest text-[#8A9A86]">Coverage</p>
                    <h2 className="font-heading text-2xl font-medium text-[#1A4331] mt-1 mb-5">Top Clinical Areas</h2>
                    <div className="space-y-3" data-testid="area-breakdown">
                        {stats.area_breakdown.map((a) => {
                            const pct = Math.round((a.count / stats.total_drugs) * 100);
                            return (
                                <div key={a.area}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="text-[#1E293B]">{a.area}</span>
                                        <span className="text-[#475569] font-mono">{a.count}</span>
                                    </div>
                                    <div className="h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                                        <div className="h-full bg-[#1A4331] rounded-full" style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>

            <Card className="mt-6 p-6 bg-gradient-to-br from-[#1A4331] to-[#123022] border-[#1A4331] text-white fade-in-up stagger-4">
                <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                        <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-white/70">AI Intelligence</p>
                        <h3 className="font-heading text-xl font-medium mt-1">Generate spend likelihood on demand</h3>
                        <p className="text-white/80 mt-2 text-sm leading-relaxed max-w-2xl">
                            Claude Sonnet 4.5 analyses each drug — clinical area, company spending trend, launch proximity, ABPI compliance —
                            and outputs evidence-backed Medical Affairs and Promotion scores with RCN product recommendations.
                        </p>
                    </div>
                </div>
            </Card>
        </div>
    );
}
